// The rules of the "Adaugă mișcare de stoc" dialog, pure (spec 054 FR-007,
// FR-014, FR-016). The operator enters the lot's stock level after the
// movement; the difference against the previous stock is the movement, and the
// reason must agree with its sign (research R1). The rules the dialog cannot
// decide alone, a duplicate row and the correction window, need the draft or
// the server and are not here.
import {
  COMMENT_REQUIRED_REASONS,
  REASON_DIRECTION,
  reasonAllowedFor,
  reasonsAllowedFor,
  startingReasonFor,
  stockMovementReasonSchema,
  type MovementDirection,
  type RegistrarKind,
  type StockMovementReason,
} from '@simsm/shared-types';
// The one lot limit, shared with core: 40, the stored column's width, which the
// owner kept on 2026-09-14 rather than rebuild the views that depend on it (R13).
// The comment's, 500, is likewise its column's (SEC-11: checked here and in core).
import { COMMENT_MAX_LENGTH, LOT_MAX_LENGTH } from '@simsm/shared-types';
import { lastDayOfMonth } from '@simsm/ui-shared';

// The nine reasons, in the wireframe's order with its "Comercializare/Eliberare"
// split into SALE and DISPENSING: each code is a reason of its own (NC-TERM-04,
// CF-15). Every reason is stored as the operator chose it, among those the active
// Registrar's type allows (UC50-15).
export const MOVEMENT_REASONS: readonly StockMovementReason[] = stockMovementReasonSchema.options;

export type MovementSign = 'increase' | 'decrease' | 'none';

export interface DefaultReasons {
  increase: StockMovementReason;
  decrease: StockMovementReason;
}

/**
 * FR-007: what a new direction starts from until the draft remembers a choice
 * (caiet UC50 step 2: a medical institution dispenses, everyone else sells; each
 * type starts from a reason it allows). `registrarKind` is the ACTIVE Registrar's
 * type, which GET /stock/locations names (docs/shape-ops-contract-codes.md
 * §Decision 7); the session's distinct kinds cannot say which of its Registrars
 * the report is for. Null until that type is known: no row starts from a reason
 * of no type (docs/shape-ops-registrar-kind-required.md §Decision 6).
 */
export function startingDefaultReasons(registrarKind: RegistrarKind): DefaultReasons;
export function startingDefaultReasons(registrarKind: RegistrarKind | null): DefaultReasons | null;
export function startingDefaultReasons(registrarKind: RegistrarKind | null): DefaultReasons | null {
  if (!registrarKind) return null;
  return {
    increase: startingReasonFor('increase', registrarKind),
    decrease: startingReasonFor('decrease', registrarKind),
  };
}

export function difference(newQty: number, previousQty: number): number {
  return newQty - previousQty;
}

export function signOf(diff: number | null): MovementSign {
  if (diff === null || diff === 0) return 'none';
  return diff > 0 ? 'increase' : 'decrease';
}

/**
 * The reasons the select offers: those of the Registrar's type, in the CF-15 table's
 * order (inflows, outflows, then Corectare; TC-UC50-15-01), filtered by the sign of
 * the difference once there is one. None without a type, as core allows none.
 */
export function reasonsFor(
  sign: MovementSign,
  registrarKind?: string | null,
): StockMovementReason[] {
  return reasonsAllowedFor(registrarKind).filter((reason) => reasonFitsSign(reason, sign));
}

export function reasonFitsSign(reason: StockMovementReason, sign: MovementSign): boolean {
  const direction = REASON_DIRECTION[reason];
  return sign === 'none' || direction === 'either' || direction === sign;
}

/**
 * The reason after the difference changed sign: the chosen one while it still
 * fits, otherwise the default of the new direction (the wireframe's
 * useRaportNou §2). No difference changes nothing, because every reason fits it.
 */
export function reasonForSign(
  current: StockMovementReason | '',
  sign: MovementSign,
  defaults: DefaultReasons,
): StockMovementReason | '' {
  if (sign === 'none') return current;
  if (current !== '' && reasonFitsSign(current, sign)) return current;
  return defaults[sign];
}

export function reasonLabelKey(reason: StockMovementReason): string {
  return `stock_reporting.reason.${reason}`;
}

export function commentRequiredFor(reason: StockMovementReason): boolean {
  return COMMENT_REQUIRED_REASONS.includes(reason);
}

export type MovementField = 'medicine' | 'lot' | 'lotExpiry' | 'quantity' | 'reason' | 'comment';

/** The dialog's field order, which is also the order focus goes to the first error in. */
export const MOVEMENT_FIELD_ORDER: readonly MovementField[] = [
  'medicine',
  'lot',
  'lotExpiry',
  'quantity',
  'reason',
  'comment',
];

/**
 * A Tolgee key, plus the reason the sentence names when it names one. The
 * caller translates that reason with `reasonLabelKey` and passes it as
 * `{reason}`.
 */
export interface FieldMessage {
  key: string;
  reason?: StockMovementReason | undefined;
}

export type MovementErrors = Partial<Record<MovementField, FieldMessage>>;
export type MovementWarnings = Partial<Record<MovementField, FieldMessage>>;

export interface MovementFormInput {
  lot: string;
  /** What the expiry field committed: `YYYY-MM`, `YYYY-MM-DD`, or '' while incomplete. */
  lotExpiry: string;
  /** The new stock level; null while the field is empty. */
  quantity: number | null;
  previousQuantity: number;
  reason: StockMovementReason | '';
  comment: string;
  /** No movement exists for this medicine and lot at this location (research R2). */
  lotIsNew: boolean;
  /**
   * False while the previous stock is unknown: the report has no location yet
   * (owner decision, 2026-09-15). `previousQuantity` then means nothing, so
   * the rules that read the difference, no difference and a reason against its
   * direction, wait for the server, which checks the row again once a location
   * is bound. Absent is true.
   */
  previousKnown?: boolean | undefined;
  medicineActive: boolean;
  isCorrection?: boolean | undefined;
  /**
   * The active Registrar's type (UC50-15): a reason it does not allow is refused,
   * as core refuses it. Null while no type is known, which allows no reason but
   * Corectare, the one every type allows.
   */
  registrarKind: RegistrarKind | null;
}

const EXPIRY_RE = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/;

const ERROR = {
  lotRequired: 'stock_reporting.movement.error.lot_required',
  lotTooLong: 'stock_reporting.movement.error.lot_too_long',
  expiryFormat: 'stock_reporting.movement.error.expiry_format',
  quantity: 'stock_reporting.movement.error.quantity',
  noDifference: 'stock_reporting.movement.error.no_difference',
  reasonRequired: 'stock_reporting.movement.error.reason_required',
  reasonNotIncrease: 'stock_reporting.movement.error.reason_not_increase',
  reasonNotDecrease: 'stock_reporting.movement.error.reason_not_decrease',
  reasonNotAllowed: 'stock_reporting.movement.error.reason_not_allowed',
  commentRequired: 'stock_reporting.movement.error.comment_required',
  commentTooLong: 'stock_reporting.movement.error.comment_too_long',
  medicineInactive: 'stock_reporting.movement.error.medicine_inactive',
} as const;

const WARNING = {
  existingLotIncrease: 'stock_reporting.movement.warning.existing_lot_increase',
  newLotPastExpiry: 'stock_reporting.movement.warning.new_lot_past_expiry',
} as const;

/** The expiry as the stored day: a bare month is its last day (UC50-02). Null when malformed. */
function expiryDay(value: string): string | null {
  const match = EXPIRY_RE.exec(value);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  if (match[3] === undefined) return lastDayOfMonth(value);
  const day = Number(match[3]);
  return day >= 1 && day <= 31 ? value : null;
}

/**
 * Whether an expiry typed for a lot is another date than the lot's own (UC50-04,
 * CF-02), a bare month read as its last day as core reads it. False while the
 * typed one is incomplete or malformed, or the lot has none: there is no
 * second date to speak of (docs/shape-ops-existing-lot-alerts.md §Decision 3).
 */
export function expiryDiffers(typed: string, known: string | null | undefined): boolean {
  if (!known) return false;
  const day = expiryDay(typed);
  return day !== null && day !== known;
}

function validQuantity(quantity: number | null): quantity is number {
  return quantity !== null && Number.isInteger(quantity) && quantity >= 0;
}

export function validateMovement(input: MovementFormInput): MovementErrors {
  const errors: MovementErrors = {};

  // Whether the lot is new here is known only at a location (research R2).
  if (
    !input.medicineActive &&
    input.previousKnown !== false &&
    input.lotIsNew &&
    input.isCorrection !== true
  ) {
    errors.medicine = { key: ERROR.medicineInactive };
  }

  const lot = input.lot.trim();
  if (lot === '') errors.lot = { key: ERROR.lotRequired };
  else if (lot.length > LOT_MAX_LENGTH) errors.lot = { key: ERROR.lotTooLong };

  if (expiryDay(input.lotExpiry) === null) errors.lotExpiry = { key: ERROR.expiryFormat };

  // Null when there is no difference to read: the quantity is not a level, or
  // the previous stock is not known yet.
  const previousKnown = input.previousKnown !== false;
  const sign =
    validQuantity(input.quantity) && previousKnown
      ? signOf(difference(input.quantity, input.previousQuantity))
      : null;
  if (!validQuantity(input.quantity)) errors.quantity = { key: ERROR.quantity };
  else if (sign === 'none') errors.quantity = { key: ERROR.noDifference };

  if (input.reason === '') {
    errors.reason = { key: ERROR.reasonRequired };
  } else {
    // A row stored with a reason the type does not allow (through the API, or
    // before the table) opens with it; the operator has to change it, whatever its
    // direction, so the sign is not blamed as well. Corectare is every type's
    // (UC50 step 8, CF-15), so it needs none: a correction is confirmed while the
    // type is still being read or could not be read, and core judges it by the
    // type it resolves (docs/shape-ops-registrar-kind-required.md §Decision 4).
    if (input.reason !== 'CORRECTION' && !reasonAllowedFor(input.reason, input.registrarKind)) {
      errors.reason = { key: ERROR.reasonNotAllowed };
    } else if (sign !== null && !reasonFitsSign(input.reason, sign)) {
      errors.reason = {
        key: sign === 'increase' ? ERROR.reasonNotIncrease : ERROR.reasonNotDecrease,
        reason: input.reason,
      };
    }
    if (commentRequiredFor(input.reason) && input.comment.trim() === '') {
      errors.comment = { key: ERROR.commentRequired, reason: input.reason };
    }
  }
  // The dialog sends the comment trimmed, so that is the length core judges
  // (docs/shape-ops-row-validation.md §Decision 5).
  if (errors.comment === undefined && input.comment.trim().length > COMMENT_MAX_LENGTH) {
    errors.comment = { key: ERROR.commentTooLong };
  }

  return errors;
}

export function firstInvalidField(errors: MovementErrors): MovementField | undefined {
  return MOVEMENT_FIELD_ORDER.find((field) => errors[field] !== undefined);
}

/**
 * The two warnings of FR-014; neither blocks the row. `today` is `YYYY-MM-DD`,
 * passed in so the rule stays pure.
 */
export function movementWarnings(input: MovementFormInput, today: string): MovementWarnings {
  const warnings: MovementWarnings = {};
  const correction = input.isCorrection === true;

  // The wireframe's condition (piese.tsx, the Diferența hint): a rise over a
  // stock that exists, outside a correction.
  if (
    !correction &&
    input.previousKnown !== false &&
    !input.lotIsNew &&
    input.previousQuantity > 0 &&
    validQuantity(input.quantity) &&
    input.quantity > input.previousQuantity
  ) {
    warnings.quantity = { key: WARNING.existingLotIncrease };
  }

  // Whether the lot is new is known only at a location (research R2).
  const expiry = expiryDay(input.lotExpiry);
  if (input.previousKnown !== false && input.lotIsNew && expiry !== null && expiry < today) {
    warnings.lotExpiry = { key: WARNING.newLotPastExpiry };
  }

  return warnings;
}

type Direction = Exclude<MovementSign, 'none'>;

/**
 * The defaults the draft remembers (R4). A direction nobody used yet, a remembered
 * code that does not fit its direction, or one the Registrar's type does not allow
 * (a default remembered before the reason table), starts from FR-007's default, as
 * core's defaultReasonFor does.
 */
export function defaultsOf(
  stored: Partial<Record<Direction, StockMovementReason>> | null | undefined,
  starting: DefaultReasons,
  registrarKind: RegistrarKind,
): DefaultReasons {
  const pick = (sign: Direction): StockMovementReason => {
    const code = stored?.[sign];
    return code && reasonFitsSign(code, sign) && reasonAllowedFor(code, registrarKind)
      ? code
      : starting[sign];
  };
  return { increase: pick('increase'), decrease: pick('decrease') };
}

/**
 * The direction a new movement starts from before its level is typed (caiet UC50
 * step 2): the one the draft stores, else decrease, the wireframe's start
 * ("implicit Comercializare/Eliberare"). The wire sends '' for none
 * (docs/shape-ops-default-reasons.md §Decision 3).
 */
export function defaultDirectionOf(
  stored: { direction?: string | undefined } | null | undefined,
): MovementDirection {
  return stored?.direction === 'increase' ? 'increase' : 'decrease';
}

/**
 * FR-007: the reason just used becomes its direction's default for the next
 * row. Corectare fits either direction, so it is nobody's default; and with no
 * difference there is no direction to remember it for.
 */
export function rememberReason(
  defaults: DefaultReasons,
  reason: StockMovementReason,
  sign: MovementSign,
): DefaultReasons {
  if (sign === 'none' || reason === 'CORRECTION' || !reasonFitsSign(reason, sign)) return defaults;
  if (defaults[sign] === reason) return defaults;
  return { ...defaults, [sign]: reason };
}
